/**
 * FindAPlace — public rentals directory. SEO-first (no auth) at
 * /find-a-place (and /find-a-place/:city). Sends live traffic to
 * landlord listings.
 */
import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { MapPin, ArrowRight, Loader2, Home } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import { usePageTitle } from "@/lib/seo";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export default function FindAPlace() {
  const { city } = useParams();
  const [params, setParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minBeds, setMinBeds] = useState(params.get("beds") || "");
  const [maxRent, setMaxRent] = useState(params.get("max") || "");

  const cityName = city ? decodeURIComponent(city) : "";
  usePageTitle(cityName
    ? `Rentals in ${cityName} — Central Rentals Canada`
    : "Find a rental in Canada — Central Rentals");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const query = new URLSearchParams();
        if (cityName) query.set("city", cityName);
        if (minBeds) query.set("min_beds", minBeds);
        if (maxRent) query.set("max_rent", maxRent);
        const [dRes, cRes] = await Promise.all([
          fetch(`${API}/api/tenant-placement/public?${query.toString()}`).then((r) => r.json()),
          fetch(`${API}/api/tenant-placement/public/cities`).then((r) => r.json()),
        ]);
        setListings(dRes?.listings || []);
        setCities(cRes?.cities || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [cityName, minBeds, maxRent]);

  // Update JSON-LD for the ItemList so Google can index the directory.
  useEffect(() => {
    const id = "find-a-place-jsonld";
    document.getElementById(id)?.remove();
    if (listings.length === 0) return;
    const s = document.createElement("script");
    s.id = id;
    s.type = "application/ld+json";
    s.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": cityName ? `Rentals in ${cityName}` : "Canadian rental directory",
      "itemListElement": listings.slice(0, 20).map((l, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "url": `https://centralrentals.ca/rental/${l.slug}`,
        "name": l.headline,
      })),
    });
    document.head.appendChild(s);
    return () => { document.getElementById(id)?.remove(); };
  }, [listings, cityName]);

  const applyFilters = (e) => {
    e?.preventDefault?.();
    const next = new URLSearchParams(params);
    if (minBeds) next.set("beds", minBeds); else next.delete("beds");
    if (maxRent) next.set("max", maxRent); else next.delete("max");
    setParams(next);
  };

  // JSON-LD schemas — ItemList of listings + FAQPage for zero-click Google
  // rich cards. Also adds meta description via document.head so crawlers
  // that don't execute JS still index a keyword-rich summary.
  useEffect(() => {
    // Meta description
    const md = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta");
      m.setAttribute("name", "description");
      document.head.appendChild(m);
      return m;
    })();
    md.setAttribute("content", cityName
      ? `Rentals in ${cityName} listed directly by verified Canadian landlords. No agents, no listing fees, provincial-standard leases. Browse ${listings.length}+ homes and apply in minutes on Central Rentals Canada.`
      : "Browse Canadian rentals listed directly by verified landlords. No agents, no listing fees, provincial-standard leases, transparent screening. Central Rentals — Canada's landlord-first rental directory.");
    // FAQPage JSON-LD
    const fid = "find-a-place-faq-jsonld";
    document.getElementById(fid)?.remove();
    const fs = document.createElement("script");
    fs.id = fid;
    fs.type = "application/ld+json";
    fs.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        { "@type": "Question", "name": "Are Central Rentals listings from real landlords?",
          "acceptedAnswer": { "@type": "Answer", "text": "Yes. Every listing on Central Rentals is posted by a landlord who has verified their identity and a paid Central Rentals subscription. There are no third-party brokers or scraped duplicates." } },
        { "@type": "Question", "name": "Does Central Rentals charge tenants a fee?",
          "acceptedAnswer": { "@type": "Answer", "text": "No. Browsing and contacting landlords through Central Rentals is free for tenants. Optional identity + credit screening (Certn + Equifax) is available; you'd only pay if you choose to run a report on yourself." } },
        { "@type": "Question", "name": "How do I apply for a rental found here?",
          "acceptedAnswer": { "@type": "Answer", "text": "Open any listing and use the contact form on the right rail. Your message goes straight to the landlord's Central Rentals inbox — they can then invite you to a formal application with a provincial-standard lease template." } },
        { "@type": "Question", "name": "What provinces does Central Rentals cover?",
          "acceptedAnswer": { "@type": "Answer", "text": "All 13 Canadian provinces and territories, including Ontario, British Columbia, Alberta, Quebec, Manitoba, Saskatchewan, Nova Scotia, New Brunswick, Newfoundland, PEI, Yukon, Northwest Territories, and Nunavut." } },
      ],
    });
    document.head.appendChild(fs);
    return () => { document.getElementById(fid)?.remove(); };
  }, [cityName, listings.length]);

  return (
    <div className="min-h-screen bg-white" data-testid="find-a-place-page">
      <PublicHeader light />
      <section className="max-w-5xl mx-auto px-5 pt-10 pb-6">
        <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] font-bold tracking-wide text-emerald-800">
          <MapPin className="w-3.5 h-3.5"/> Verified Canadian landlords · No listing fees
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900" data-testid="find-a-place-h1">
          {cityName ? `Rentals in ${cityName}` : "Find your next rental — direct from Canadian landlords"}
        </h1>
        <p className="mt-4 text-lg text-slate-600 max-w-2xl leading-relaxed" data-testid="find-a-place-lede">
          {cityName
            ? `Every ${cityName} listing here is posted by a landlord who has verified their identity on Central Rentals. No agents, no listing fees, no scraped duplicates.`
            : "Every listing is posted by a verified Canadian landlord. Skip the agents, skip the listing fees, and message the person who actually owns the home."}
        </p>

        {/* Trust-signal strip — crawlable static HTML, not JS-rendered */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="find-a-place-trust-strip">
          {[
            { k: "Verified", v: "Landlord ID" },
            { k: "0%", v: "Tenant fees" },
            { k: "13", v: "Provinces covered" },
            { k: "Direct", v: "Message the landlord" },
          ].map((t) => (
            <div key={t.k} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
              <div className="font-display text-xl font-extrabold text-emerald-700 tabular-nums">{t.k}</div>
              <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">{t.v}</div>
            </div>
          ))}
        </div>

        <form onSubmit={applyFilters} className="mt-6 flex flex-wrap gap-2 items-end" data-testid="find-a-place-filters">
          <label className="text-xs">
            <div className="font-bold text-slate-700 mb-1">Min bedrooms</div>
            <input type="number" min="0" value={minBeds} onChange={(e) => setMinBeds(e.target.value)} placeholder="Any" className="w-24 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm" data-testid="filter-beds"/>
          </label>
          <label className="text-xs">
            <div className="font-bold text-slate-700 mb-1">Max rent (CAD/mo)</div>
            <input type="number" min="200" value={maxRent} onChange={(e) => setMaxRent(e.target.value)} placeholder="Any" className="w-32 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm" data-testid="filter-rent"/>
          </label>
          <button type="submit" data-testid="filter-apply" className="px-3.5 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold">Apply</button>
        </form>
      </section>

      <section className="max-w-5xl mx-auto px-5 pb-16">
        {loading && (
          <div className="flex items-center justify-center py-16 text-slate-500" data-testid="find-a-place-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2"/> Loading rentals…
          </div>
        )}

        {!loading && listings.length === 0 && (
          <div className="p-10 text-center rounded-2xl border-2 border-dashed border-slate-200" data-testid="find-a-place-empty">
            <Home className="w-8 h-8 mx-auto text-slate-300"/>
            <div className="mt-3 font-display text-lg font-bold text-slate-800">No listings match right now</div>
            <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">Try widening your filters — or check back later. Fresh listings appear daily.</p>
          </div>
        )}

        {!loading && listings.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="find-a-place-grid">
            {listings.map((l) => (
              <Link
                key={l.slug}
                to={`/rental/${l.slug}`}
                data-testid={`listing-card-${l.slug}`}
                className="block bg-white rounded-xl border border-slate-200 hover:border-emerald-400 hover:shadow-lg transition-all overflow-hidden"
              >
                {l.photos?.[0] ? (
                  <img src={l.photos[0]} alt={l.headline} loading="lazy" className="w-full h-40 object-cover"/>
                ) : (
                  <div className="w-full h-40 bg-gradient-to-br from-emerald-50 to-slate-100 flex items-center justify-center">
                    <Home className="w-8 h-8 text-slate-400"/>
                  </div>
                )}
                <div className="p-4">
                  {l.boosted_until && <span className="inline-block text-[10px] font-bold uppercase tracking-widest text-amber-900 bg-amber-100 px-2 py-0.5 rounded-full mb-1">Featured</span>}
                  <div className="font-display font-bold text-slate-900 text-sm line-clamp-2">{l.headline}</div>
                  <div className="mt-1 text-xs text-slate-500">{l.address_public || l.city}</div>
                  <div className="mt-2 flex items-baseline justify-between">
                    <span className="text-lg font-extrabold text-emerald-700 tabular-nums">${(l.rent_cad || 0).toLocaleString()}</span>
                    <span className="text-[11px] text-slate-500">
                      {[
                        l.bedrooms != null ? `${l.bedrooms} bed` : null,
                        l.bathrooms != null ? `${l.bathrooms} bath` : null,
                      ].filter(Boolean).join(" · ")}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Cities strip — SEO deep-links */}
        {cities.length > 0 && (
          <div className="mt-14" data-testid="find-a-place-cities">
            <h2 className="font-display text-lg font-bold text-slate-900">Popular cities</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {cities.map((c) => (
                <Link
                  key={c.city}
                  to={`/find-a-place/${encodeURIComponent(c.city)}`}
                  data-testid={`city-link-${c.city.toLowerCase().replace(/\s+/g,'-')}`}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-50 hover:bg-emerald-50 border border-slate-200 hover:border-emerald-300 text-xs font-semibold text-slate-800"
                >
                  {c.city} <span className="text-slate-400">· {c.listings}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Benefits section — crawlable H2/H3, keyword-dense body copy */}
        <div className="mt-14 grid md:grid-cols-3 gap-5" data-testid="find-a-place-benefits">
          <div>
            <h2 className="font-display text-base font-extrabold text-slate-900">Direct-to-landlord messaging</h2>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">Your enquiry goes straight to the landlord&apos;s Central Rentals inbox — not to a lead-broker who resells your details.</p>
          </div>
          <div>
            <h2 className="font-display text-base font-extrabold text-slate-900">Provincial-standard leases</h2>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">Every landlord on Central Rentals uses the official provincial lease template — Ontario N-forms, BC RTB agreements, Alberta RTA — so you know your rights are protected.</p>
          </div>
          <div>
            <h2 className="font-display text-base font-extrabold text-slate-900">Transparent screening</h2>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">Screening runs on Certn + Equifax with full FCRA-style consent. You always get your own copy of the report — no data resold, no follow-up spam.</p>
          </div>
        </div>

        {/* Visible FAQ mirrors the FAQPage JSON-LD above so Google's rich-
           card grader treats the answers as authoritative for the page. */}
        <div className="mt-14 max-w-3xl" data-testid="find-a-place-faq">
          <h2 className="font-display text-2xl font-extrabold text-slate-900">Frequently asked questions</h2>
          <dl className="mt-4 divide-y divide-slate-200">
            {[
              { q: "Are Central Rentals listings from real landlords?", a: "Yes. Every listing on Central Rentals is posted by a landlord who has verified their identity and a paid Central Rentals subscription. There are no third-party brokers or scraped duplicates." },
              { q: "Does Central Rentals charge tenants a fee?", a: "No. Browsing and contacting landlords through Central Rentals is free for tenants. Optional identity + credit screening is available; you'd only pay if you choose to run a report on yourself." },
              { q: "How do I apply for a rental found here?", a: "Open any listing and use the contact form on the right rail. Your message goes straight to the landlord's Central Rentals inbox — they can then invite you to a formal application with a provincial-standard lease template." },
              { q: "What provinces does Central Rentals cover?", a: "All 13 Canadian provinces and territories, including Ontario, British Columbia, Alberta, Quebec, Manitoba, Saskatchewan, Nova Scotia, New Brunswick, Newfoundland, PEI, Yukon, Northwest Territories, and Nunavut." },
            ].map((f, i) => (
              <div key={i} className="py-4" data-testid={`fap-faq-${i}`}>
                <dt className="font-bold text-slate-900">{f.q}</dt>
                <dd className="mt-2 text-sm text-slate-700 leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>

        {/* Landlord CTA — converts landlord traffic that lands on tenant page */}
        <div className="mt-14 max-w-3xl bg-slate-900 text-white rounded-3xl p-8 md:p-10" data-testid="find-a-place-landlord-cta">
          <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-400">Are you a landlord?</div>
          <h2 className="mt-2 font-display text-2xl md:text-3xl font-extrabold">List your rental in front of Canadian tenants</h2>
          <p className="mt-2 text-slate-300 leading-relaxed max-w-xl">
            Publish an unlimited number of vacant units to this directory on any Central Rentals paid plan. No listing fees, no per-view charges, no lead resale.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/signup?role=landlord&plan=starter" data-testid="fap-landlord-signup" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-extrabold text-sm">
              Start listing from CA$35/mo
            </Link>
            <Link to="/pricing" data-testid="fap-landlord-pricing" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border-2 border-white/20 hover:border-white/40 text-white font-extrabold text-sm">
              See landlord pricing
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
